import {
    ChangeDetectionStrategy,
    Component,
    Input,
    EventEmitter,
    OnInit,
    Output,
    ViewChild,
    ViewEncapsulation,
    AfterViewInit,
    ElementRef,
} from '@angular/core';
import { ICommentModel } from '@fi-shared/fi-comments/fi-comments';
import { FiCommentsListItemEditComponent } from '@fi-shared/fi-comments/fi-comments-list/fi-comments-list-item-edit/fi-comments-list-item-edit.component';
import { AppTextService } from '@fi-shared/app-text';
import { isDefined } from '@fi-shared/utils/is-defined';
import { FiContenteditableComponent } from '@fi-app/shared/fi-contenteditable/fi-contenteditable.component';
import { FiCommentsListStateService } from '../services/fi-comments-list-state.service';
import { FiCommentsListHelperService } from '../services/fi-comments-list-helper.service';
import { ObjectTypes } from '@fi-shared/types';
import { TextEditorTagUsersService } from '@fi-shared/fi-text-editor/fi-text-editor-helper-services/text-editor-tag-users.service';

@Component({
    selector: 'fi-comment-add',
    template: `
        <fi-comments-list-item-edit
            [comment]="newComment"
            [saveButtonText]="saveButtonText"
            [isCancelButtonShown]="isCancelButtonShown"
            [isNewComment]="!isReply"
            [isCancelButtonShown]="isReply"
            [autofocus]="autofocus"
            [objectId]="objectId"
            [objectType]="objectType"
            [instanceId]="instanceId"
            (closeEditor)="onCloseEditor()"
        />
    `,
    styleUrls: ['./fi-comment-add.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-comment-add' },
    standalone: false,
})
export class FiCommentAddComponent implements OnInit, AfterViewInit {
    @Input({ required: true }) objectId: number;
    @Input({ required: true }) objectType: ObjectTypes;
    @Input({ required: true }) instanceId: string;

    @Input() saveButtonText: string = this.appTextService.get('common.reply');
    @Input() parentCommentId: number;
    @Input() autofocus: boolean;
    @Input() isCancelButtonShown: boolean = true;
    @Input() isReply: boolean = false;
    @Output() commentSaved = new EventEmitter<ICommentModel>();
    @Output() commentEditCancelled = new EventEmitter<void>();

    newComment: ICommentModel;

    @ViewChild(FiCommentsListItemEditComponent, { static: true })
    commentsListItemEditComponent: FiCommentsListItemEditComponent;

    constructor(
        private appTextService: AppTextService,
        private textEditorTagUsersService: TextEditorTagUsersService,
        private commentsListStateService: FiCommentsListStateService,
        private commentsListHelperService: FiCommentsListHelperService,
    ) {}

    public ngOnInit(): void {
        this.setNewComment();
    }

    private setNewComment(): void {
        this.newComment = this.commentsListHelperService.getNewCommentInstance(
            this.objectId,
            this.objectType,
            this.parentCommentId,
        );
    }

    private getParentComment(parentCommentId: number): ICommentModel {
        return this.commentsListStateService
            .getFlattenComments(this.objectType, this.objectId)
            .find((comment) => comment.Id === parentCommentId);
    }

    public ngAfterViewInit(): void {
        if (!isDefined(this.parentCommentId)) {
            return;
        }

        const parentComment = this.getParentComment(this.parentCommentId);
        if (!isDefined(parentComment)) {
            return;
        }
        // tag user
        this.setTag(parentComment);
    }

    private get editorElRef(): ElementRef<HTMLElement> {
        return this.FiContenteditableComponent.contenteditableElRef;
    }

    private get FiContenteditableComponent(): FiContenteditableComponent {
        return this.commentsListItemEditComponent.simpleContenteditableComponent.contenteditableComponent;
    }

    private setTag(parentComment: ICommentModel): void {
        this.editorElRef.nativeElement.innerHTML = '@1';
        this.textEditorTagUsersService.insertTag(
            this.editorElRef.nativeElement,
            {
                Id: parentComment.CreatedByUserId,
                FullName: parentComment.CreatedByUserName,
                MediaBankPhysicalFileName: parentComment.UserProfile?.MediaBankPhysicalFileName,
                MediaBankExtension: parentComment.UserProfile?.MediaBankExtension,
            },
            () => {
                this.FiContenteditableComponent.triggerChangeEvent();
            },
        );
    }

    protected onCloseEditor(): void {
        this.commentEditCancelled.emit();
    }

    public setFocus() {
        // todo is it best way bring focus to contenteditable?
        this.editorElRef.nativeElement.focus();
    }
}
